import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Button, Form, Modal } from 'react-bootstrap';
import './Profile.css';
import axios from 'axios';
import config from '../includes/ConfigFile';
import { Link, useNavigate } from 'react-router-dom';
import getUser from '../includes/UserHelper';

const Profile = () => {
    const [isEditing, setIsEditing] = useState(false);
    const [showLibraryModal, setShowLibraryModal] = useState(false);
    const [showPublishedModal, setShowPublishedModal] = useState(false);
    const [publisheData, setPublisheData] = useState([]);
    const [draftData, setDraftData] = useState([]);

    const navigate = useNavigate();
    const fileInputRef = useRef(null);
    const [selectedPostId, setSelectedPostId] = useState(null);

    const [profileData, setProfileData] = useState({
        name: '',
        email: '',
        phone_number: '',
        bio: '',
        profile_picture: '',
        date_of_birth: '',
        gender: ''
    });

    useEffect(() => {
        const user = getUser();
        if (user && user.id) {
            fetchUserDetails(user.id);
        } else {
            navigate('/login');
        }
    }, []);

    const fetchUserDetails = async (userId) => {
        try {
            const response = await axios.get(`${config.BASE_URL}api/getUserDetails/${userId}`);
            if (response.data && response.data.user) {
                setProfileData(response.data.user);
            } else {
                alert('User not found');
            }
        } catch (error) {
            console.error('Error fetching user details:', error);
        }
    };

    //For Published Posts
    useEffect(() => {
        const user = getUser();
        if (user && user.id) {
            axios.get(`${config.BASE_URL}/api/publishPost/${user.id}`)
                .then(response => setPublisheData(response.data))
                .catch(error => console.error('Error fetching published posts:', error));
        }
    }, []);

    //For Draft Posts
    useEffect(() => {
        const user = getUser();
        if (user && user.id) {
            axios.get(`${config.BASE_URL}/api/draftPost/${user.id}`)
                .then(response => setDraftData(response.data))
                .catch(error => console.error('Error fetching draft posts:', error));
        }
    }, []);

    const handleEdit = () => setIsEditing(true);

    const handleSave = async () => {
        try {
            const user = getUser();
            if (!user || !user.id) return alert('User not found. Please log in again.');

            const updatedData = {
                id: user.id,
                name: profileData.name,
                email: profileData.email,
                phone_number: profileData.phone_number,
                bio: profileData.bio,
                date_of_birth: profileData.date_of_birth,
                gender: profileData.gender
            };

            const response = await axios.post(`${config.BASE_URL}/api/updateUserDetails`, updatedData);

            if (response.data.message) {
                alert('Profile updated successfully');
                setIsEditing(false);
            } else {
                alert('Failed to update profile');
            }
        } catch (error) {
            console.error('Error updating profile:', error);
            alert('An error occurred while updating profile');
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setProfileData(prev => ({ ...prev, [name]: value }));
    };

    // --- Profile Photo Upload ---
    const handlePhotoClick = () => {
        setSelectedPostId(null); // profile ke liye clear karna hoga
        fileInputRef.current.click();
    };

    const handlePhotoChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const user = getUser();
        const formData = new FormData();
        formData.append('profile_picture', file);
        formData.append('user_id', user.id);

        try {
            const response = await axios.post(`${config.BASE_URL}api/uploadProfilePicture`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (response.data.success) {
                alert('Profile photo updated successfully!');
                fetchUserDetails(user.id);
            } else {
                alert('Failed to update photo');
            }
        } catch (error) {
            console.error('Error uploading photo:', error);
            alert('Error uploading profile photo');
        }
    };

    // --- Post Image Upload (Published + Draft) ---
    const handlePostImageClick = (postId, type) => {
        setSelectedPostId({ id: postId, type });
        fileInputRef.current.click();
    };

    const handlePostImageChange = async (e) => {
        const file = e.target.files[0];
        if (!file || !selectedPostId) return;

        const user = getUser();
        const formData = new FormData();
        formData.append('post_image', file);
        formData.append('user_id', user.id);
        formData.append('post_id', selectedPostId.id);

        try {
            const response = await axios.post(`${config.BASE_URL}api/updatePostImage`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (response.data.success) {
                alert('Post image updated successfully!');
                if (selectedPostId.type === "published") {
                    axios.get(`${config.BASE_URL}/api/publishPost/${user.id}`)
                        .then(res => setPublisheData(res.data));
                } else {
                    axios.get(`${config.BASE_URL}/api/draftPost/${user.id}`)
                        .then(res => setDraftData(res.data));
                }
            } else {
                alert('Failed to update post image');
            }
        } catch (error) {
            console.error('Error uploading post image:', error);
            alert('Error uploading post image');
        }
    };

    const localUser = getUser();
    const profileImage = profileData.profile_picture
        ? profileData.profile_picture.startsWith('http')
            ? profileData.profile_picture
            : `${config.BASE_URL}${profileData.profile_picture}`
        : `${process.env.PUBLIC_URL}/uploads/profile/default.png`;

    return (
        <Container className="profile-container my-5">
            <Row>
                <Col md={4}>
                    <Card className="profile-card">
                        <div className="profile-avatar">
                            <img src={profileImage} alt="Profile" />
                            <div className="avatar-overlay">
                                <Button variant="link" onClick={handlePhotoClick}>Change Photo</Button>
                                <input
                                    type="file"
                                    accept="image/*"
                                    ref={fileInputRef}
                                    style={{ display: 'none' }}
                                    onChange={selectedPostId ? handlePostImageChange : handlePhotoChange}
                                />
                            </div>
                        </div>
                        <Card.Body className="text-center">
                            <h4>{profileData.name}</h4>
                            <p className="text-muted">Member since {new Date(profileData.created_date).getFullYear()}</p>
                        </Card.Body>
                    </Card>
                    <Card className="profile-card mt-5 text-center">
                        <Link to="/writer"><Button variant="link"><i className="bi bi-pencil-square"></i> Write</Button></Link>
                        <Button variant="link" onClick={() => setShowLibraryModal(true)}>
                            <i className="bi bi-bookmark-star"></i> Library / Draft
                        </Button>
                        <Button variant="link" onClick={() => setShowPublishedModal(true)}>
                            <i className="bi bi-journals"></i> Published Works
                        </Button>

                        {/* Draft Modal */}
                        <Modal show={showLibraryModal} onHide={() => setShowLibraryModal(false)} size="lg">
                            <Modal.Header closeButton>
                                <Modal.Title style={{ color: '#000' }}>Draft Works</Modal.Title>
                            </Modal.Header>
                            <Modal.Body style={{ color: '#000' }}>
                                {draftData.length === 0 ? (
                                    <p>No drafts found.</p>
                                ) : (
                                    <Container>
                                        <Row>
                                            {draftData.map((item) => (
                                                <Col key={item.id} md={4} className="mb-4">
                                                    <Card className="shadow-sm rounded">
                                                        <Card.Img
                                                            variant="top"
                                                            src={`${config.BASE_URL}public/uploads/admin/post/${item.post_image}`}
                                                            alt={item.post_title}
                                                            style={{ height: '200px', objectFit: 'cover', borderRadius: '0.5rem 0.5rem 0 0', cursor: 'pointer' }}
                                                            onClick={() => handlePostImageClick(item.id, "draft")}
                                                        />
                                                        <Card.Body className="text-center">
                                                            <Card.Title style={{ fontSize: '1rem', fontWeight: 'bold' }}>
                                                                {item.post_title}
                                                            </Card.Title>
                                                            <div className="d-flex justify-content-center gap-2 mt-3">
                                                                <Button
                                                                    variant="primary"
                                                                    size="sm"
                                                                    onClick={() => navigate(`/writer/${item.id}`)}
                                                                >
                                                                    ➕ Continue Writing
                                                                </Button>
                                                                <Button
                                                                    variant="secondary"
                                                                    size="sm"
                                                                    onClick={() => navigate(`/editChapters/${item.id}`)}
                                                                >
                                                                    ✏️ Edit Draft
                                                                </Button>
                                                            </div>
                                                        </Card.Body>
                                                    </Card>
                                                </Col>
                                            ))}
                                        </Row>
                                    </Container>
                                )}
                            </Modal.Body>
                        </Modal>

                        {/* Published Works Modal */}
                        <Modal show={showPublishedModal} onHide={() => setShowPublishedModal(false)} size="lg">
                            <Modal.Header closeButton>
                                <Modal.Title style={{ color: '#000' }}>Published Works</Modal.Title>
                            </Modal.Header>
                            <Modal.Body style={{ color: '#000' }}>
                                {publisheData.length === 0 ? (
                                    <p>No published works found.</p>
                                ) : (
                                    <Container>
                                        <Row>
                                            {publisheData.map((item) => (
                                                <Col key={item.id} md={4} className="mb-4">
                                                    <Card className="shadow-sm rounded">
                                                        <Card.Img
                                                            variant="top"
                                                            src={`${config.BASE_URL}public/uploads/admin/post/${item.post_image}`}
                                                            alt={item.post_title}
                                                            style={{ height: '200px', objectFit: 'cover', borderRadius: '0.5rem 0.5rem 0 0', cursor: 'pointer' }}
                                                            onClick={() => handlePostImageClick(item.id, "published")}
                                                        />
                                                        <Card.Body className="text-center">
                                                            <Card.Title style={{ fontSize: '1rem', fontWeight: 'bold' }}>
                                                                {item.post_title}
                                                            </Card.Title>
                                                            <div className="d-flex justify-content-center gap-2 mt-3">
                                                                <Button
                                                                    variant="primary"
                                                                    size="sm"
                                                                    onClick={() => navigate(`/writer/${item.id}`)}
                                                                >
                                                                    ➕ Add Chapter
                                                                </Button>
                                                                <Button
                                                                    variant="secondary"
                                                                    size="sm"
                                                                    onClick={() => navigate(`/editChapters/${item.id}`)}
                                                                >
                                                                    ✏️ Edit Chapter
                                                                </Button>
                                                            </div>
                                                        </Card.Body>
                                                    </Card>
                                                </Col>
                                            ))}
                                        </Row>
                                    </Container>
                                )}
                            </Modal.Body>
                        </Modal>
                    </Card>
                </Col>

                <Col md={8}>
                    <Card className="profile-details">
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">Profile Details</h5>
                            <Button variant={isEditing ? "success" : "primary"} onClick={isEditing ? handleSave : handleEdit}>
                                {isEditing ? "Save Changes" : "Edit Profile"}
                            </Button>
                        </Card.Header>
                        <Card.Body>
                            <Form className='profile-form'>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Full Name</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="name"
                                                value={profileData.name}
                                                onChange={handleChange}
                                                disabled={!isEditing}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Email</Form.Label>
                                            <Form.Control
                                                type="email"
                                                name="email"
                                                value={profileData.email}
                                                onChange={handleChange}
                                                disabled
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Phone Number</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="phone_number"
                                                value={profileData.phone_number}
                                                onChange={handleChange}
                                                disabled={!isEditing}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>DOB</Form.Label>
                                            <Form.Control
                                                type="date"
                                                name="date_of_birth"
                                                value={profileData.date_of_birth}
                                                onChange={handleChange}
                                                disabled={!isEditing}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Gender</Form.Label>
                                            <Form.Select
                                                name="gender"
                                                value={profileData.gender || ''}
                                                onChange={handleChange}
                                                disabled={!isEditing}
                                            >
                                                <option value="">Select Gender</option>
                                                <option value="male">Male</option>
                                                <option value="female">Female</option>
                                                <option value="other">Other</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={12}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Bio</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={4}
                                                name="bio"
                                                value={profileData.bio}
                                                onChange={handleChange}
                                                disabled={!isEditing}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default Profile;
